import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useArchivedClients,
  useClients,
  useClientStats,
  useClientTiers,
} from '@busy/api';
import { ClientDetailModal } from '../components/ClientDetailModal';
import { NewClientModal } from '../components/NewClientModal';
import { AlphaClientList, type ChipKey } from '../components/clients/AlphaClientList';
import { ClientTiersChips } from '../components/clients/ClientTiersChips';
import { ClientPipelineTile } from '../components/clients/ClientPipelineTile';
import { useT } from '../lib/i18n';

// Clients tab — the canonical roster surface. Stack from top to bottom:
//   1. Header (title + roster count)
//   2. Client tiers breakdown — doubles as the filter UI for the alpha
//      list below. Lives at the very top so the barber can pick a slice
//      first ("show me my VIPs") before diving into the lists. Tapping
//      a row narrows the AlphaClientList directly — no separate chip
//      strip needed.
//   3. At-risk clients list (collapsible) — most-overdue clients first;
//      daily-action item the barber should clear before browsing
//   4. Top clients (collapsible) — highest-revenue clients
//   5. Apple-Contacts-style alpha list (filtered by selected tier)

export function Clients() {
  const clients = useClients();
  const archived = useArchivedClients();
  const stats = useClientStats();
  const tiers = useClientTiers();
  const t = useT();

  // Track the open client by ID — derive the live row from clients/archived
  // so edits reflect immediately without close + reopen.
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [showNewClient, setShowNewClient] = useState(false);

  // Filter selection — lifted up so the ClientTiersChips (above) can act
  // as the filter control. Tapping a tier row narrows the alpha list
  // below. Default 'all' shows every active client.
  const [chip, setChip] = useState<ChipKey>('all');

  // Optional `?focus=topClients` deep-link from the Dashboard's VIP
  // Clients metric card. After mount we scroll the Top Clients tile
  // into view and pulse-highlight it for ~2.4s.
  const [searchParams] = useSearchParams();
  const focusParam = searchParams.get('focus');
  const topClientsRef = useRef<HTMLDivElement>(null);
  const [focusFlash, setFocusFlash] = useState(false);
  useEffect(() => {
    if (focusParam !== 'topClients') return;
    const raf = requestAnimationFrame(() => {
      topClientsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setFocusFlash(true);
      const timer = setTimeout(() => setFocusFlash(false), 2400);
      return () => clearTimeout(timer);
    });
    return () => cancelAnimationFrame(raf);
  }, [focusParam]);

  const statsByClient = useMemo(() => {
    const map = new Map(((stats.data ?? []).map((s) => [s.client_id, s])));
    return map;
  }, [stats.data]);

  const tierByClient = useMemo(() => {
    const map = new Map(((tiers.data ?? []).map((tt) => [tt.client_id, tt.tier])));
    return map;
  }, [tiers.data]);

  const selectedClient = useMemo(() => {
    const all = [...(clients.data ?? []), ...(archived.data ?? [])];
    return all.find((c) => c.id === selectedClientId) ?? null;
  }, [clients.data, archived.data, selectedClientId]);

  return (
    <>
      <div className="max-w-page mx-auto p-4 md:p-6 space-y-4 pb-24">
        <header className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-4xl md:text-[42px] text-navy-900 dark:text-gold-500 leading-[1.05]">
              {t('clients.title')}
            </h1>
            {clients.data && (
              <p className="text-[15px] text-ink-500 mt-1.5">
                {clients.data.length === 1
                  ? t('clients.roster_one')
                  : t('clients.roster_other', { count: clients.data.length })}
              </p>
            )}
          </div>
        </header>

        {/* Roster — compact chip-strip filter + alpha list. Lookup
            is the 80% job on this tab so it stays at the top. */}
        <ClientTiersChips selected={chip} onSelect={setChip} />
        <AlphaClientList
          onOpenClient={setSelectedClientId}
          onAddNewClient={() => setShowNewClient(true)}
          maxHeight="55vh"
          chip={chip}
        />

        {/* Your Book — the consolidated client-lifecycle tile.
            Two-column overview at top (acquisition channels +
            retention metrics) over three collapsible action lists
            (drifting clients, upcoming birthdays, top clients).
            Replaces the four standalone tiles (Retention,
            Acquisition, Birthdays, Top Clients) that lived here
            previously. */}
        <div
          ref={topClientsRef}
          className={`scroll-mt-20 rounded-2xl transition-shadow ${
            focusFlash
              ? 'ring-2 ring-gold-500 ring-offset-2 ring-offset-bg shadow-[0_0_0_4px_rgba(212,169,66,0.18)]'
              : ''
          }`}
        >
          <ClientPipelineTile onOpenClient={setSelectedClientId} />
        </div>
      </div>

      {selectedClient && (
        <ClientDetailModal
          client={selectedClient}
          stats={statsByClient.get(selectedClient.id)}
          tier={tierByClient.get(selectedClient.id)}
          onClose={() => setSelectedClientId(null)}
        />
      )}

      {showNewClient && (
        <NewClientModal
          onClose={() => setShowNewClient(false)}
          onOpenExisting={(id) => setSelectedClientId(id)}
        />
      )}
    </>
  );
}
